import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import type { AlternativeSlot } from '@lov/shared';
import { getAvailabilityIndicator } from '@lov/shared';

interface SlotUnavailableModalProps {
  open: boolean;
  errorMessage: string;
  alternatives: AlternativeSlot[];
  totalCapacity: number;
  onSelectSlot: (slot: AlternativeSlot) => void;
  onClose: () => void;
}

export function SlotUnavailableModal({
  open,
  errorMessage,
  alternatives,
  totalCapacity,
  onSelectSlot,
  onClose,
}: SlotUnavailableModalProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
        <Typography variant="h6" component="div">
          Slot No Longer Available
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
          {errorMessage}
        </Typography>

        {alternatives.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
              Available alternatives:
            </Typography>
            <List>
              {alternatives.map((slot, index) => {
                const indicator = getAvailabilityIndicator(slot.available, totalCapacity);
                return (
                  <ListItemButton
                    key={index}
                    onClick={() => onSelectSlot(slot)}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <FiberManualRecordIcon sx={{ fontSize: 12, color: indicator.color }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${slot.startTime} - ${slot.endTime}`}
                      secondary={`${slot.available} spots left`}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </>
        )}

        {alternatives.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No alternative slots available for this date.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          Choose Different Date
        </Button>
      </DialogActions>
    </Dialog>
  );
}
